import React, { Component } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Dimensions
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { fontFamily } from '../../helpers/fontFamily';
import { LinearGradient } from 'expo-linear-gradient';

class PPMTestDataGenerator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      response: null,
      error: null,
      windowWidth: Dimensions.get('window').width
    };
  }

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  updateDimensions = () => {
    this.setState({ windowWidth: Dimensions.get('window').width });
  };

  generateTestData = async (type) => {
    this.setState({ loading: true, error: null, response: null });

    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const tenent = await AsyncStorage.getItem('tenent');
      const new_time_zone = await AsyncStorage.getItem('new_time_zone');

      const response = await axios.post(
        `https://api-gl.groove-packer.com/dashboard/generate_ppm_test_data`,
        {
          type: type,
          timezone: new_time_zone || 'UTC'
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${access_token}`,
            domain: 'groovepackerapi.com',
            tenant: tenent
          }
        }
      );

      this.setState({ loading: false, response: response.data });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.response?.data?.error || error.message || 'Failed to generate test data'
      });
    }
  };

  resetTestData = async () => {
    this.setState({ loading: true, error: null, response: null });

    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const tenent = await AsyncStorage.getItem('tenent');

      const response = await axios.post(
        `https://api-gl.groove-packer.com/dashboard/reset_test_data`,
        {},
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${access_token}`,
            domain: 'groovepackerapi.com',
            tenant: tenent
          }
        }
      );

      this.setState({ loading: false, response: response.data });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.response?.data?.error || error.message || 'Failed to reset test data'
      });
    }
  };

  renderButton = (title, type, color) => {
    return (
      <TouchableOpacity
        style={[styles.button, { backgroundColor: color }]}
        onPress={() => this.generateTestData(type)}
        disabled={this.state.loading}>
        <Text style={styles.buttonText}>{title}</Text>
      </TouchableOpacity>
    );
  };

  renderResponse = () => {
    const { response } = this.state;
    if (!response) return null;

    return (
      <View style={styles.responseContainer}>
        <Text style={styles.responseTitle}>{response.message}</Text>
        
        {response.type && (
          <View style={styles.infoRow}>
            <Text style={styles.label}>Type:</Text>
            <Text style={styles.value}>{response.type}</Text>
          </View>
        )}
        
        {response.description && (
          <View style={styles.infoRow}>
            <Text style={styles.label}>Description:</Text>
            <Text style={styles.value}>{response.description}</Text>
          </View>
        )}

        {response.summary && (
          <View style={styles.summaryContainer}>
            <Text style={styles.summaryTitle}>Summary:</Text>

            {response.summary.duration_days && (
              <Text style={styles.summaryText}>Duration: {response.summary.duration_days} days</Text>
            )}

            {response.summary.total_items !== undefined && (
              <Text style={styles.summaryText}>Total Items: {response.summary.total_items}</Text>
            )}

            {response.summary.total_orders !== undefined && (
              <Text style={styles.summaryText}>Total Orders: {response.summary.total_orders}</Text>
            )}

            {response.summary.items_per_day && (
              <Text style={styles.summaryText}>Items Per Day: {response.summary.items_per_day}</Text>
            )}

            {response.summary.spike_day && (
              <Text style={styles.summaryText}>Spike Day: {response.summary.spike_day}</Text>
            )}

            {response.summary.spike_items && (
              <Text style={styles.summaryText}>Spike Items: {response.summary.spike_items}</Text>
            )}

            {response.summary.baseline_items_per_day && (
              <Text style={styles.summaryText}>Baseline: {response.summary.baseline_items_per_day} items/day</Text>
            )}

            {response.summary.error_rate_percentage !== undefined && (
              <Text style={styles.summaryText}>Error Rate: {response.summary.error_rate_percentage}%</Text>
            )}

            {response.summary.successful_scans !== undefined && (
              <Text style={styles.summaryText}>Successful Scans: {response.summary.successful_scans}</Text>
            )}

            {response.summary.failed_scans !== undefined && (
              <Text style={styles.summaryText}>Failed Scans: {response.summary.failed_scans}</Text>
            )}

            {response.summary.breakdown_by_day && (
              <View style={styles.breakdownContainer}>
                <Text style={styles.breakdownTitle}>Breakdown by Day:</Text>
                <ScrollView style={styles.breakdownScroll}>
                  {response.summary.breakdown_by_day.map((day, index) => (
                    <View key={index} style={styles.dayRow}>
                      <Text style={styles.dayDate}>{day.date}</Text>
                      <Text style={styles.dayInfo}>
                        Items: {day.items} | Orders: {day.orders} | Minutes: {day.session_minutes}
                      </Text>
                      {day.successful_scans !== undefined && (
                        <Text style={styles.dayInfo}>
                          Success: {day.successful_scans} | Failed: {day.failed_scans}
                        </Text>
                      )}
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
        )}

        {response.deleted && (
          <View style={styles.summaryContainer}>
            <Text style={styles.summaryTitle}>Deleted:</Text>
            <Text style={styles.summaryText}>StatStreams: {response.deleted.stat_streams}</Text>
            <Text style={styles.summaryText}>User Activities: {response.deleted.user_daily_activities}</Text>
            <Text style={styles.summaryText}>Daily Stats: {response.deleted.daily_stats}</Text>
            <Text style={styles.summaryText}>User: {response.deleted.user ? 'Yes' : 'No'}</Text>
          </View>
        )}
      </View>
    );
  };

  render() {
    const { loading, error } = this.state;

    return (
      <ScrollView style={styles.container}>
        <View style={styles.content}>
          <Text style={styles.title}>PPM Test Data Generator</Text>
          <Text style={styles.subtitle}>Generate test data for PPM dashboard testing</Text>

          <View style={styles.buttonsContainer}>
            {this.renderButton('Normal Scenario', 'normal', '#4CAF50')}
            {this.renderButton('High Volume', 'high', '#FF9800')}
            {this.renderButton('Heavy/Error', 'heavy', '#F44336')}
            {this.renderButton('Zero Activity', 'zero', '#9E9E9E')}
          </View>

          <TouchableOpacity
            style={[styles.button, styles.resetButton]}
            onPress={this.resetTestData}
            disabled={loading}>
            <Text style={styles.buttonText}>Reset Test Data</Text>
          </TouchableOpacity>

          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#336599" />
              <Text style={styles.loadingText}>Processing...</Text>
            </View>
          )}

          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>Error: {error}</Text>
            </View>
          )}

          {this.renderResponse()}
        </View>
      </ScrollView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1a1a1a'
  },
  content: {
    padding: 20,
    alignItems: 'center'
  },
  title: {
    fontSize: 28,
    fontFamily: fontFamily.font700,
    color: '#fff',
    marginBottom: 10
  },
  subtitle: {
    fontSize: 16,
    fontFamily: fontFamily.font400,
    color: '#999',
    marginBottom: 30,
    textAlign: 'center'
  },
  buttonsContainer: {
    width: '100%',
    maxWidth: 600,
    marginBottom: 20
  },
  button: {
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    alignItems: 'center'
  },
  resetButton: {
    backgroundColor: '#d32f2f',
    width: '100%',
    maxWidth: 600
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: fontFamily.font700
  },
  loadingContainer: {
    marginTop: 20,
    alignItems: 'center'
  },
  loadingText: {
    color: '#999',
    marginTop: 10,
    fontFamily: fontFamily.font400
  },
  errorContainer: {
    backgroundColor: '#ffebee',
    padding: 15,
    borderRadius: 8,
    marginTop: 20,
    width: '100%',
    maxWidth: 600
  },
  errorText: {
    color: '#c62828',
    fontFamily: fontFamily.font400
  },
  responseContainer: {
    backgroundColor: '#2a2a2a',
    padding: 20,
    borderRadius: 8,
    marginTop: 20,
    width: '100%',
    maxWidth: 800
  },
  responseTitle: {
    fontSize: 20,
    fontFamily: fontFamily.font700,
    color: '#4CAF50',
    marginBottom: 15
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 10
  },
  label: {
    fontFamily: fontFamily.font700,
    color: '#999',
    marginRight: 10,
    minWidth: 100
  },
  value: {
    fontFamily: fontFamily.font400,
    color: '#fff',
    flex: 1
  },
  summaryContainer: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#444'
  },
  summaryTitle: {
    fontSize: 18,
    fontFamily: fontFamily.font700,
    color: '#fff',
    marginBottom: 10
  },
  summaryText: {
    fontFamily: fontFamily.font400,
    color: '#ccc',
    marginBottom: 5
  },
  breakdownContainer: {
    marginTop: 15
  },
  breakdownTitle: {
    fontSize: 16,
    fontFamily: fontFamily.font700,
    color: '#fff',
    marginBottom: 10
  },
  breakdownScroll: {
    maxHeight: 300
  },
  dayRow: {
    backgroundColor: '#333',
    padding: 10,
    borderRadius: 5,
    marginBottom: 8
  },
  dayDate: {
    fontFamily: fontFamily.font700,
    color: '#4CAF50',
    marginBottom: 5
  },
  dayInfo: {
    fontFamily: fontFamily.font400,
    color: '#ccc',
    fontSize: 12
  }
});

export default PPMTestDataGenerator;

